/**
 * Invoice domain constants: kinds, statuses, numbering and — critically — the
 * supported print dimensions. A page size is never hardcoded in CSS or in a
 * component; the print layout resolves it from `INVOICE_PAGE_SIZES`.
 */

import type { Locale } from "./config";

export interface InvoicePageSize {
  id: string;
  label: string;
  widthMm: number;
  heightMm: number;
  /** Value used verbatim for the CSS `@page { size: … }` rule. */
  cssPageSize: string;
  /** Preview width in CSS pixels at 96 dpi. */
  previewWidthPx: number;
  /** Thermal receipt layout: single narrow column, no page chrome. */
  isReceipt: boolean;
}

export const INVOICE_PAGE_SIZES = {
  a4: {
    id: "a4",
    label: "A4",
    widthMm: 210,
    heightMm: 297,
    cssPageSize: "210mm 297mm",
    previewWidthPx: 794,
    isReceipt: false,
  },
  letter: {
    id: "letter",
    label: "Letter",
    widthMm: 215.9,
    heightMm: 279.4,
    cssPageSize: "215.9mm 279.4mm",
    previewWidthPx: 816,
    isReceipt: false,
  },
  receipt80: {
    id: "receipt80",
    label: "Receipt 80mm",
    widthMm: 80,
    heightMm: 297,
    cssPageSize: "80mm auto",
    previewWidthPx: 302,
    isReceipt: true,
  },
} as const satisfies Record<string, InvoicePageSize>;

export type InvoicePageSizeId = keyof typeof INVOICE_PAGE_SIZES;

/** Tuple form so zod enums and selectors keep the literal union. */
export const INVOICE_PAGE_SIZE_IDS = ["a4", "letter", "receipt80"] as const;

export const ALL_INVOICE_PAGE_SIZES: readonly InvoicePageSize[] = Object.values(INVOICE_PAGE_SIZES);

export const DEFAULT_INVOICE_PAGE_SIZE: InvoicePageSizeId = "a4";

export const INVOICE_PAGE_SIZE_LABEL_PREFIX = "Page size" as const;

export const INVOICE_KINDS = {
  full: "full",
  downPayment: "down_payment",
  balance: "balance",
  adjustment: "adjustment",
} as const;

export type InvoiceKind = (typeof INVOICE_KINDS)[keyof typeof INVOICE_KINDS];

export const INVOICE_KIND_LABELS: Record<InvoiceKind, string> = {
  full: "Invoice",
  down_payment: "Down payment invoice",
  balance: "Balance invoice",
  adjustment: "Adjustment invoice",
};

export const INVOICE_STATUSES = {
  issued: "issued",
  paid: "paid",
  void: "void",
} as const;

export type InvoiceStatus = (typeof INVOICE_STATUSES)[keyof typeof INVOICE_STATUSES];

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  issued: "Issued",
  paid: "Paid",
  void: "Void",
};

export const INVOICE_NUMBERING = {
  prefix: "INV",
  separator: "-",
  padding: 4,
  maxSequenceRetries: 3,
} as const;

export const INVOICE_ASSET_FORMATS = {
  png: { extension: "png", mime: "image/png" },
  jpeg: { extension: "jpg", mime: "image/jpeg" },
} as const;

export type InvoiceAssetFormat = keyof typeof INVOICE_ASSET_FORMATS;

export const DEFAULT_INVOICE_ASSET_FORMAT: InvoiceAssetFormat = "png";

export const INVOICE_IMAGE_SCALE = 2 as const;

export const INVOICE_PUBLIC_LINK_SEGMENT = "invoices" as const;

export const INVOICE_SHARE_PLACEHOLDERS = {
  customer: "{customer}",
  invoiceNumber: "{invoiceNumber}",
  businessName: "{businessName}",
  amount: "{amount}",
  url: "{url}",
} as const;

export interface InvoiceShareContext {
  customerName: string;
  invoiceNumber: string;
  businessName: string;
  amount: string;
  url: string;
}

export const INVOICE_SHARE_TEMPLATES: Record<
  Locale,
  { whatsapp: string; emailSubject: string; emailBody: string }
> = {
  en: {
    whatsapp:
      "Hi {customer}, here is invoice {invoiceNumber} from {businessName} for {amount}. View or download it here: {url}",
    emailSubject: "Invoice {invoiceNumber} from {businessName}",
    emailBody:
      "Hi {customer},\n\nPlease find invoice {invoiceNumber} for {amount} attached below.\n\n{url}\n\nThank you,\n{businessName}",
  },
  id: {
    whatsapp:
      "Halo {customer}, berikut invoice {invoiceNumber} dari {businessName} sebesar {amount}. Lihat atau unduh di sini: {url}",
    emailSubject: "Invoice {invoiceNumber} dari {businessName}",
    emailBody:
      "Halo {customer},\n\nBerikut invoice {invoiceNumber} sebesar {amount}.\n\n{url}\n\nTerima kasih,\n{businessName}",
  },
};

export const WHATSAPP_SHARE_ORIGIN = "https://wa.me" as const;
export const WHATSAPP_MESSAGE_PARAM = "text" as const;
export const EMAIL_SCHEME = "mailto:" as const;

export const INVOICE_PRINT_STYLES = {
  pageMarginMm: 10,
  bodyFontPt: 10,
  receiptFontPt: 8,
  headerFontPt: 16,
} as const;
